import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessagesSquare, MailQuestion, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/EmptyState';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import type { OutreachDraft } from '@/lib/api-types';
import { cn } from '@/lib/cn';

interface Thread {
  key: string;
  recipientEmail: string | null;
  recipientName: string | null;
  drafts: OutreachDraft[];
  latest: OutreachDraft;
  unreadCount: number;
}

type StatusFilter = 'all' | 'sent' | 'replied' | 'bounced';

export default function Messages() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['outreach-threads'],
    queryFn: api.listThreadDrafts,
  });

  const drafts = data?.drafts ?? [];
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const threads = useMemo<Thread[]>(() => {
    const byKey = new Map<string, OutreachDraft[]>();
    for (const d of drafts) {
      const key = d.recipient_email?.toLowerCase() ?? `candidate:${d.candidate_property_id}`;
      const list = byKey.get(key) ?? [];
      list.push(d);
      byKey.set(key, list);
    }
    return Array.from(byKey.entries())
      .map(([key, list]) => {
        const sorted = [...list].sort(
          (a, b) => (b.sent_at ?? b.created_at) - (a.sent_at ?? a.created_at)
        );
        const latest = sorted[0]!;
        return {
          key,
          recipientEmail: latest.recipient_email,
          recipientName: latest.recipient_name,
          drafts: sorted,
          latest,
          unreadCount: sorted.filter((d) => d.status === 'replied').length,
        };
      })
      .sort((a, b) => (b.latest.sent_at ?? b.latest.created_at) - (a.latest.sent_at ?? a.latest.created_at));
  }, [drafts]);

  const filtered = useMemo(() => {
    if (filter === 'all') return threads;
    return threads.filter((t) => t.drafts.some((d) => d.status === filter));
  }, [threads, filter]);

  const selected = filtered.find((t) => t.key === selectedKey) ?? filtered[0] ?? null;

  const counts = useMemo(() => {
    let sent = 0, replied = 0, bounced = 0;
    for (const d of drafts) {
      if (d.status === 'sent' || d.status === 'approved') sent += 1;
      else if (d.status === 'replied') replied += 1;
      else if (d.status === 'bounced') bounced += 1;
    }
    return { sent, replied, bounced };
  }, [drafts]);

  return (
    <div className="flex flex-col gap-6">
      <Masthead
        kicker="Relations"
        title="Messages"
        dateline={
          isLoading
            ? 'Loading conversations…'
            : `${threads.length} conversation${threads.length === 1 ? '' : 's'} · ${counts.replied} reply${counts.replied === 1 ? '' : ' replies'}`
        }
        lede="Threaded view of every broker and owner you've reached. Approvals happen inside the solicitation — replies land back here."
      />

      {isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Failed to load messages: {error instanceof Error ? error.message : 'unknown'}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <FilterPill label={`All (${threads.length})`} active={filter === 'all'} onClick={() => setFilter('all')} />
        <FilterPill label={`Sent (${counts.sent})`} active={filter === 'sent'} onClick={() => setFilter('sent')} />
        <FilterPill
          label={`Replied (${counts.replied})`}
          active={filter === 'replied'}
          onClick={() => setFilter('replied')}
          highlight={counts.replied > 0}
        />
        <FilterPill label={`Bounced (${counts.bounced})`} active={filter === 'bounced'} onClick={() => setFilter('bounced')} />
      </div>

      {!isLoading && threads.length === 0 ? (
        <EmptyState
          title="No conversations yet"
          description="Once you approve outreach from a solicitation page and a broker replies, the thread will land here. Approvals live on the Solicitation → Outreach tab."
          icon={<MessagesSquare className="h-5 w-5" />}
        />
      ) : (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]"
          data-testid="messages-view"
        >
          <aside className="card overflow-hidden">
            <div className="border-b border-rule bg-surface-sunk px-4 py-3">
              <div className="text-[0.6875rem] font-semibold uppercase tracking-kicker text-ink-muted">
                {filtered.length} threads
              </div>
            </div>
            {isLoading ? (
              <div className="flex flex-col gap-3 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <SkeletonLine key={i} width={`${65 + ((i * 7) % 30)}%`} />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="grid place-items-center p-10 text-sm text-ink-muted">
                No threads match this filter.
              </div>
            ) : (
              <ul className="max-h-[72vh] overflow-y-auto scroll-elegant">
                {filtered.map((t) => (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => setSelectedKey(t.key)}
                      className={cn(
                        'flex w-full flex-col gap-1.5 border-b border-rule px-4 py-3.5 text-left transition-colors hover:bg-surface-sunk',
                        selected?.key === t.key &&
                          'bg-brand-50 hover:bg-brand-50 dark:bg-brand-900/40 dark:hover:bg-brand-900/40'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div className="grid h-7 w-7 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.625rem] font-bold text-white">
                          {(t.recipientName ?? t.recipientEmail ?? '?').slice(0, 2).toUpperCase()}
                        </div>
                        <span className="flex-1 truncate text-[0.8125rem] font-semibold text-ink">
                          {t.recipientName ?? t.recipientEmail ?? 'Unknown recipient'}
                        </span>
                        {t.unreadCount > 0 && (
                          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[0.625rem] font-bold text-white">
                            {t.unreadCount}
                          </span>
                        )}
                        <span className="text-[0.6875rem] text-ink-dim">
                          {fmtRelative(t.latest.sent_at ?? t.latest.created_at)}
                        </span>
                      </div>
                      <div className="truncate text-[0.8125rem] text-ink">{t.latest.subject}</div>
                      <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                        <ThreadStatusDot status={t.latest.status} />
                        <span className="truncate">
                          Re: {t.latest.solicitation_number ?? t.latest.rlp_opportunity_id.slice(0, 8)}
                          {' · '}
                          {t.drafts.length} message{t.drafts.length === 1 ? '' : 's'}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section className="card overflow-hidden">
            {selected ? (
              <ThreadDetail thread={selected} />
            ) : (
              <div className="grid h-full place-items-center p-10 text-sm text-ink-muted">
                Select a conversation to read the thread.
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function FilterPill({
  label,
  active,
  onClick,
  highlight,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  highlight?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
        active
          ? 'border-brand-500 bg-brand-50 text-brand-700'
          : 'border-rule bg-surface text-ink-muted hover:border-rule-strong hover:text-ink',
        highlight && !active && 'border-brand-400/50 text-brand-700'
      )}
    >
      {label}
    </button>
  );
}

function ThreadStatusDot({ status }: { status: OutreachDraft['status'] }) {
  const tone =
    status === 'replied'
      ? 'bg-brand-500'
      : status === 'bounced'
        ? 'bg-danger'
        : status === 'sent' || status === 'approved'
          ? 'bg-emerald-500'
          : 'bg-ink-dim';
  return <span className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', tone)} />;
}

function ThreadDetail({ thread }: { thread: Thread }) {
  const chronological = [...thread.drafts].sort(
    (a, b) => (a.sent_at ?? a.created_at) - (b.sent_at ?? b.created_at)
  );
  return (
    <article className="flex h-full flex-col">
      <header className="border-b border-rule bg-surface-sunk px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[0.8125rem] font-bold text-white">
              {(thread.recipientName ?? thread.recipientEmail ?? '?').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink">
                {thread.recipientName ?? thread.recipientEmail ?? 'Unknown recipient'}
                {thread.latest.recipient_role && (
                  <Badge tone="neutral">{thread.latest.recipient_role}</Badge>
                )}
              </div>
              <div className="truncate text-xs text-ink-muted">
                {thread.recipientEmail ?? 'no email on file'}
              </div>
            </div>
          </div>
          <Link
            to={`/rlps/${thread.latest.rlp_opportunity_id}`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-rule bg-surface px-3 py-1.5 text-xs font-semibold text-brand-700 hover:border-brand-400/50"
          >
            View solicitation
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.75rem] text-ink-muted">
          <span>
            Re: <span className="font-semibold text-ink">{thread.latest.solicitation_number ?? thread.latest.rlp_opportunity_id.slice(0, 8)}</span>
          </span>
          <span>· {thread.latest.candidate_address ?? 'Property pending'}</span>
          <span>· {thread.drafts.length} message{thread.drafts.length === 1 ? '' : 's'}</span>
        </div>
      </header>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5 scroll-elegant">
        {chronological.map((d) => (
          <MessageBubble key={d.id} draft={d} />
        ))}
        <InboundPlaceholder />
      </div>
    </article>
  );
}

function MessageBubble({ draft }: { draft: OutreachDraft }) {
  const ts = draft.sent_at ?? draft.approved_at ?? draft.created_at;
  const statusBadge = statusDisplay(draft.status);
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-rule bg-surface px-4 py-3 shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-ink-muted">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-ink">You</span>
          <span>→ {draft.recipient_email ?? draft.recipient_name ?? 'recipient'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={statusBadge.tone}>{statusBadge.label}</Badge>
          <span>{fmtRelative(ts)}</span>
        </div>
      </div>
      <div className="text-[0.9375rem] font-semibold text-ink">{draft.subject}</div>
      <div className="whitespace-pre-wrap text-[0.875rem] leading-relaxed text-ink">{draft.body}</div>
      {draft.status === 'bounced' && (
        <div className="rounded-lg border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger-bold">
          Mail bounced — address may be stale. Verify via the candidate property profile.
        </div>
      )}
    </div>
  );
}

function InboundPlaceholder() {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-dashed border-rule bg-surface-sunk px-4 py-3 text-xs text-ink-muted">
      <MailQuestion className="h-4 w-4 text-ink-dim" />
      Inbound replies thread here once Nylas webhooks are wired. For now, check your mailbox and mark replies manually if needed.
    </div>
  );
}

function statusDisplay(
  status: OutreachDraft['status']
): { label: string; tone: 'green' | 'red' | 'yellow' | 'brand' | 'neutral' } {
  switch (status) {
    case 'replied':
      return { label: 'Replied', tone: 'brand' };
    case 'sent':
      return { label: 'Sent', tone: 'green' };
    case 'approved':
      return { label: 'Approved · sending', tone: 'yellow' };
    case 'bounced':
      return { label: 'Bounced', tone: 'red' };
    case 'rejected':
      return { label: 'Rejected', tone: 'neutral' };
    default:
      return { label: status, tone: 'neutral' };
  }
}
